// Premium brand logo monograms used in the experience-page stat cards.
// All marks rendered in champagne-gold/cream monochrome to match the site
// design. For iconic geometric brands (Audi, BMW) we ship simplified SVG
// recreations; for the rest we use stylized wordmark typography.
//
// These are simplified, monochrome reinterpretations used for portfolio
// reference of clients & institutions Karim has worked with — they are not
// official brand assets.

const COLOR = "currentColor";

// ─── Iconic geometric marks ────────────────────────────────────────────────

const AudiRings = () => (
  <svg viewBox="0 0 120 32" fill="none" stroke={COLOR} strokeWidth="2" aria-label="Audi">
    {/* 4 interlocking rings */}
    <circle cx="16" cy="16" r="13" />
    <circle cx="44" cy="16" r="13" />
    <circle cx="72" cy="16" r="13" />
    <circle cx="100" cy="16" r="13" />
  </svg>
);

const BmwRoundel = () => (
  <svg viewBox="0 0 32 32" fill="none" stroke={COLOR} strokeWidth="1.5" aria-label="BMW">
    <circle cx="16" cy="16" r="14" />
    <circle cx="16" cy="16" r="10" />
    {/* quartered pattern */}
    <path d="M16 6 L16 26" />
    <path d="M6 16 L26 16" />
    <path d="M16 6 A10 10 0 0 1 26 16 L16 16 Z" fill={COLOR} fillOpacity="0.85" stroke="none" />
    <path d="M16 16 A10 10 0 0 1 6 16 A10 10 0 0 1 16 26 Z" fill={COLOR} fillOpacity="0.85" stroke="none" />
  </svg>
);

const IstqbMark = () => (
  <svg viewBox="0 0 120 32" fill="none" aria-label="ISTQB">
    {/* hex badge + wordmark */}
    <path d="M16 4 L26 10 L26 22 L16 28 L6 22 L6 10 Z" stroke={COLOR} strokeWidth="1.8" />
    <text x="36" y="22" fontFamily="'Manrope', sans-serif" fontWeight="700" fontSize="14" fill={COLOR} letterSpacing="2">
      ISTQB
    </text>
  </svg>
);

const MitMark = () => (
  <svg viewBox="0 0 64 32" fill={COLOR} aria-label="MIT">
    {/* Stylised MIT stripes (homage to MIT logo's columnar look) */}
    <rect x="0" y="4" width="6" height="24" />
    <rect x="10" y="4" width="6" height="24" />
    <rect x="20" y="4" width="6" height="24" />
    <rect x="30" y="4" width="6" height="14" />
    <rect x="40" y="4" width="6" height="24" />
    <rect x="50" y="4" width="14" height="6" />
    <rect x="54" y="10" width="6" height="18" />
  </svg>
);

const LmuMark = () => (
  <svg viewBox="0 0 130 32" fill="none" aria-label="LMU München">
    {/* Stylised LMU wordmark + München label */}
    <text x="0" y="22" fontFamily="'Instrument Serif', 'Times New Roman', serif" fontStyle="italic" fontWeight="600" fontSize="22" fill={COLOR} letterSpacing="1">
      LMU
    </text>
    <text x="44" y="22" fontFamily="'Manrope', sans-serif" fontWeight="400" fontSize="12" fill={COLOR} fillOpacity="0.8" letterSpacing="2">
      MÜNCHEN
    </text>
  </svg>
);

// ─── Wordmark brands (styled text) ─────────────────────────────────────────

const CariadMark = () => (
  <span style={{
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: "-0.02em",
    color: COLOR,
    textTransform: "lowercase",
  }}>
    CARIAD
  </span>
);

const BertrandtMark = () => (
  <span style={{
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: "0.04em",
    color: COLOR,
    textTransform: "uppercase",
  }}>
    BERTRANDT
  </span>
);

const IcConsultMark = () => (
  <span style={{
    fontFamily: "'Instrument Serif', serif",
    fontWeight: 400,
    fontStyle: "italic",
    fontSize: 14,
    letterSpacing: "0.02em",
    color: COLOR,
  }}>
    iC consult
  </span>
);

const InTechMark = () => (
  <span style={{
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: "0.04em",
    color: COLOR,
    textTransform: "lowercase",
  }}>
    in-tech
  </span>
);

const AutoScoutMark = () => (
  <span style={{
    fontFamily: "'Manrope', sans-serif",
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: "0.02em",
    color: COLOR,
  }}>
    AutoScout24
  </span>
);

const WismarMark = () => (
  <span style={{
    fontFamily: "'Instrument Serif', serif",
    fontWeight: 500,
    fontStyle: "italic",
    fontSize: 13,
    letterSpacing: "0.04em",
    color: COLOR,
  }}>
    Wismar
  </span>
);

// ─── Public registry ───────────────────────────────────────────────────────

const REGISTRY = {
  audi: { Component: AudiRings, width: 60, label: "Audi" },
  bmw: { Component: BmwRoundel, width: 22, label: "BMW" },
  cariad: { Component: CariadMark, width: null, label: "CARIAD" },
  bertrandt: { Component: BertrandtMark, width: null, label: "Bertrandt" },
  "ic-consult": { Component: IcConsultMark, width: null, label: "iC consult" },
  "in-tech": { Component: InTechMark, width: null, label: "in-tech" },
  autoscout: { Component: AutoScoutMark, width: null, label: "AutoScout24" },
  istqb: { Component: IstqbMark, width: 70, label: "ISTQB" },
  mit: { Component: MitMark, width: 42, label: "MIT" },
  lmu: { Component: LmuMark, width: 124, label: "LMU München" },
  wismar: { Component: WismarMark, width: null, label: "Wismar" },
};

export default function BrandLogos({ items = [], className = "" }) {
  return (
    <div
      className={`flex items-center flex-wrap gap-x-4 gap-y-2 text-[var(--gold-dark)]/80 ${className}`}
      data-testid="brand-logos"
    >
      {items.map((key, idx) => {
        const entry = REGISTRY[key];
        if (!entry) return null;
        const { Component, width, label } = entry;
        return (
          <span
            key={key + idx}
            className="inline-flex items-center"
            style={{ height: 18, ...(width ? { width } : null) }}
            title={label}
            data-testid={`brand-${key}`}
          >
            <Component />
          </span>
        );
      })}
    </div>
  );
}
